import React, { useState } from "react";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import { CurrentFeesTable } from "./CurrentFeesTable";
import { ProposedFeesTable } from "./ProposedFeesTable";
import { useFeeInformationFlyer, useFeeQueryMutation } from "../../hooks/useFeeInformationFlyer";
import { FeeActionEnum, IFeeQueryRequest } from "../../types/feeInformationFlyer";

interface FeeInformationFlyerProps {
  dateAs?: string;
  isFbo?: boolean;
}

export const FeeInformationFlyer: React.FC<FeeInformationFlyerProps> = ({ dateAs }) => {
  const { data: feeData, isLoading, error, refetch } = useFeeInformationFlyer(dateAs);
  const { saveFeeQuery, isLoading: isMutating } = useFeeQueryMutation();

  const [showQueryForm, setShowQueryForm] = useState(false);
  const [queryComment, setQueryComment] = useState("Request a call from Relationship Manager");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showAcceptedMessage, setShowAcceptedMessage] = useState(false);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Error loading fee information: {error.message}</Alert>
      </Container>
    );
  }

  if (!feeData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">No fee information available.</Alert>
      </Container>
    );
  }

  const { serviceInformationFlyer, parentCommunicationDate, effectiveFromTerm, showTermInEffectiveFrom } = feeData;

  const financialYear = dayjs().month() >= 6 ? dayjs().year() : dayjs().year() - 1;
  const nextYear = dayjs().year();
  const effectiveDate = feeData.effectiveFromDate ? dayjs(feeData.effectiveFromDate) : dayjs(new Date(nextYear, 0, 1));
  const customerCommDate = parentCommunicationDate
    ? dayjs(parentCommunicationDate)
    : effectiveDate.subtract(21, "days");

  // Determine term text based on showTermInEffectiveFrom flag
  const termText = showTermInEffectiveFrom
    ? effectiveFromTerm || `Term 4, ${nextYear}`
    : effectiveDate.format("MMMM D, YYYY");

  const termName = "Term 4"; // Default term name

  const feeAction = getFeeAction(serviceInformationFlyer.acceptOrAcknowledgeFee);

  const handleAcceptFees = async () => {
    if (!serviceInformationFlyer.currentFees || !serviceInformationFlyer.proposedFees) return;

    const feeQuery: IFeeQueryRequest = {
      isAccepted: true,
      isFboAccepted: false,
      comment: "",
      currentBscFeeId: serviceInformationFlyer.currentFees.bscFeeId,
      currentBscAmount: serviceInformationFlyer.currentFees.bscFeeAmount || 0,
      currentAscFeeId: serviceInformationFlyer.currentFees.ascFeeId,
      currentAscAmount: serviceInformationFlyer.currentFees.ascFeeAmount || 0,
      currentVacFeeId: serviceInformationFlyer.currentFees.vacFeeId,
      currentVacAmount: serviceInformationFlyer.currentFees.vacFeeAmount || 0,
      proposedBscFeeId: serviceInformationFlyer.proposedFees.bscFeeId,
      proposedBscAmount: serviceInformationFlyer.proposedFees.bscFeeAmount || 0,
      proposedAscFeeId: serviceInformationFlyer.proposedFees.ascFeeId,
      proposedAscAmount: serviceInformationFlyer.proposedFees.ascFeeAmount || 0,
      proposedVacFeeId: serviceInformationFlyer.proposedFees.vacFeeId,
      proposedVacAmount: serviceInformationFlyer.proposedFees.vacFeeAmount || 0,
    };

    try {
      await saveFeeQuery(serviceInformationFlyer.serviceId, {
        query: feeQuery,
        feeAction,
        sendComms: feeData.sendComms,
      });

      setShowAcceptedMessage(true);
      refetch();
    } catch (err) {
      console.error("Failed to accept fees:", err);
    }
  };

  const handleSubmitQuery = async () => {
    if (!serviceInformationFlyer.currentFees || !serviceInformationFlyer.proposedFees) return;

    if (!queryComment.trim()) {
      alert("Please provide query detail.");
      return;
    }

    const feeQuery: IFeeQueryRequest = {
      isAccepted: false,
      isFboAccepted: false,
      comment: queryComment,
      currentBscFeeId: serviceInformationFlyer.currentFees.bscFeeId,
      currentBscAmount: serviceInformationFlyer.currentFees.bscFeeAmount || 0,
      currentAscFeeId: serviceInformationFlyer.currentFees.ascFeeId,
      currentAscAmount: serviceInformationFlyer.currentFees.ascFeeAmount || 0,
      currentVacFeeId: serviceInformationFlyer.currentFees.vacFeeId,
      currentVacAmount: serviceInformationFlyer.currentFees.vacFeeAmount || 0,
      proposedBscFeeId: serviceInformationFlyer.proposedFees.bscFeeId,
      proposedBscAmount: serviceInformationFlyer.proposedFees.bscFeeAmount || 0,
      proposedAscFeeId: serviceInformationFlyer.proposedFees.ascFeeId,
      proposedAscAmount: serviceInformationFlyer.proposedFees.ascFeeAmount || 0,
      proposedVacFeeId: serviceInformationFlyer.proposedFees.vacFeeId,
      proposedVacAmount: serviceInformationFlyer.proposedFees.vacFeeAmount || 0,
    };

    try {
      await saveFeeQuery(serviceInformationFlyer.serviceId, {
        query: feeQuery,
        feeAction: "Request a callback",
        sendComms: feeData.sendComms,
      });

      setShowSuccessMessage(true);
      setShowQueryForm(false);
      refetch();
    } catch (err) {
      console.error("Failed to submit query:", err);
    }
  };

  const getUserMessage = (action: string): string => {
    switch (action) {
      case FeeActionEnum.Accept:
        return 'If you agree to these fee adjustments, please click the "Accept" button below.';
      case FeeActionEnum.Acknowledge:
        return 'Please click the "Acknowledge" button below when you have reviewed the fees.';
      case FeeActionEnum.Reject:
        return 'If you do not agree to these fee adjustments, please click the "Reject" button below.';
      case FeeActionEnum.Contact:
        return 'If you have any queries, please click "Request a call back from your Relationship Manager"';
      default:
        return 'If you agree to these fee adjustments, please click the "Accept" button below.';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Success Messages */}
      {showAcceptedMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Thank you for {feeAction.toLowerCase() === "acknowledge" ? "acknowledging" : feeAction.toLowerCase()}ing the
            proposed fees.
          </Typography>
          <Typography>
            The new fees will take effect from the start of {termName}, {nextYear}.
          </Typography>
          <Typography sx={{ mt: 1 }}>We appreciate your support for the OSHC program and Camp Australia.</Typography>
          <Typography sx={{ mt: 1 }}>
            If you have a moment please take the opportunity to review the rest of the Partner Portal as it provides
            continuous reporting and valuable information about OSHC at {serviceInformationFlyer.serviceName}.
          </Typography>
        </Alert>
      )}

      {showSuccessMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Thank you for submitting your query. We will contact you soon.
        </Alert>
      )}

      {/* Header */}
      {!serviceInformationFlyer.hasAcceptedFees && !showAcceptedMessage && (
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: "16px",
            border: "2px solid",
            borderColor: "grey.300",
            transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
            "&:hover": {
              boxShadow: 4,
              borderColor: "primary.main",
            },
          }}
        >
          <CardHeader
            avatar={<CalendarIcon />}
            title={
              <Typography variant="h5" component="h1">
                Parent fee update at {serviceInformationFlyer.serviceName} to take effect from {termText}.
              </Typography>
            }
          />
          <CardContent>
            <Typography>
              As per our email, we would like to communicate the new fees to families at{" "}
              {serviceInformationFlyer.serviceName} on {customerCommDate.format("DD[th] MMMM YYYY")}. This will give
              them sufficient notice prior to the start of {termText}.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Current Fees - Only show if there are changes (matching legacy behavior - currently hidden in legacy) */}
      {false && serviceInformationFlyer.currentFees && feeData?.activeRolls && (
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: "16px",
            border: "2px solid",
            borderColor: "grey.300",
            transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
            "&:hover": {
              boxShadow: 4,
              borderColor: "primary.main",
            },
          }}
        >
          <CardHeader avatar={<CalendarIcon />} title={<Typography variant="h6">Current Fees and Times</Typography>} />
          <CardContent>
            <CurrentFeesTable
              currentFees={serviceInformationFlyer.currentFees!}
              activeRolls={feeData!.activeRolls}
              isFbo={serviceInformationFlyer.isFboService}
            />
          </CardContent>
        </Card>
      )}

      {/* Proposed Fees */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: "16px",
          border: "2px solid",
          borderColor: "grey.300",
          transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
          "&:hover": {
            boxShadow: 4,
            borderColor: "primary.main",
          },
        }}
      >
        <CardHeader avatar={<CalendarIcon />} title={<Typography variant="h6">Proposed Fees and Times</Typography>} />
        <CardContent>
          {serviceInformationFlyer.proposedFees ? (
            <>
              <ProposedFeesTable
                proposedFees={serviceInformationFlyer.proposedFees}
                currentFees={serviceInformationFlyer.currentFees}
                activeRolls={feeData.activeRolls}
                isFbo={serviceInformationFlyer.isFboService}
                ccsInfo={feeData.currentYearCcsInfo}
              />
              <Typography variant="caption" sx={{ mt: 2, display: "block", ml: 1 }}>
                *The amount of CCS a family may be entitled to depends on their circumstances and the hourly rate set by
                the Government.
              </Typography>
            </>
          ) : (
            <Typography variant="h6">
              There are no proposed fees are available for {serviceInformationFlyer.serviceName}.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {!serviceInformationFlyer.hasAcceptedFees &&
        !showAcceptedMessage &&
        (feeData.lastFeeQuery === null ||
          feeData.lastFeeQuery === undefined ||
          dayjs(feeData.lastFeeQuery.dateCreated).isBefore(dayjs(new Date(financialYear, 6, 1)))) && (
          <Card
            variant="outlined"
            sx={{
              borderRadius: "16px",
              border: "2px solid",
              borderColor: "grey.300",
              transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 4,
                borderColor: "primary.main",
              },
            }}
          >
            <CardContent>
              {feeAction.toLowerCase() !== "contact" && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ mb: 2 }}>{getUserMessage(feeAction)}</Typography>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleAcceptFees}
                      disabled={isMutating}
                      sx={{ mr: 2 }}
                    >
                      {isMutating ? <CircularProgress size={20} /> : feeAction}
                    </Button>
                  </Box>
                  <Box sx={{ borderTop: "1px solid #ddd", pt: 2 }} />
                </>
              )}

              <Box sx={{ pt: feeAction.toLowerCase() === "contact" ? 0 : 2 }}>
                <Typography sx={{ mb: 2 }}>
                  If you have any queries, please click "Request a call back from your Relationship Manager"
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowQueryForm(true)}
                  sx={{ mb: showQueryForm ? 2 : 0 }}
                >
                  Request a call back from your Relationship Manager
                </Button>

                {showQueryForm && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      value={queryComment}
                      onChange={(e) => setQueryComment(e.target.value)}
                      variant="outlined"
                      className="fee-query-textarea"
                      sx={{ mb: 2 }}
                    />
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitQuery}
                        disabled={isMutating}
                        sx={{ mr: 1 }}
                      >
                        {isMutating ? <CircularProgress size={20} /> : "Submit Query"}
                      </Button>
                      <Button variant="outlined" onClick={() => setShowQueryForm(false)}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

      {/* Already Accepted Message */}
      {!showAcceptedMessage &&
        (serviceInformationFlyer.hasAcceptedFees ||
          (feeData.lastFeeQuery &&
            dayjs(feeData.lastFeeQuery.dateCreated).isAfter(dayjs(new Date(financialYear, 6, 1))))) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You have <strong>{feeAction}ed</strong> fees on{" "}
            <strong>{dayjs(feeData.lastFeeQuery?.dateCreated).format("MMMM D, YYYY [at] h:mm A")}</strong>
          </Alert>
        )}
    </Container>
  );
};

// Helper function to determine fee action
function getFeeAction(dbAction?: string): string {
  const action = dbAction?.toLowerCase() || "";

  switch (action) {
    case "1":
    case "true":
    case "accept":
      return FeeActionEnum.Accept;
    default:
      return FeeActionEnum.Acknowledge;
  }
}
